// ---------------------------------------------------------------------------
// Platform module — Platform Members list page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { SearchInput } from '../components/SearchInput';
import { useFormatters } from '../hooks/useFormatters';

export function PlatformMembersPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const idpLabels = t('platformMembers.identityProviders', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.PlatformMemberSummary>[]>(
    () => [
      {
        key: 'displayName',
        header: t('platformMembers.columns.name'),
        render: (m) => (
          <span className="text-sm font-medium text-ink">
            {m.displayName ?? '--'}
          </span>
        ),
      },
      {
        key: 'email',
        header: t('platformMembers.columns.email'),
        render: (m) => (
          <span className="text-sm text-ink-muted">{m.email}</span>
        ),
      },
      {
        key: 'identityProvider',
        header: t('platformMembers.columns.identityProvider'),
        render: (m) => (
          <span className="text-sm text-ink-muted">
            {idpLabels[m.identityProvider] ?? m.identityProvider}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('platformMembers.columns.status'),
        render: (m) => <StatusBadge value={m.status} />,
      },
      {
        key: 'createdAt',
        header: t('platformMembers.columns.created'),
        render: (m) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(m.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t, idpLabels],
  );
  const [members, setMembers] = useState<admin.PlatformMemberSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadMembers = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin
      .listPlatformMembers()
      .then((res) => setMembers(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('platformMembers.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      (m) =>
        m.email.toLowerCase().includes(q) ||
        (m.displayName ?? '').toLowerCase().includes(q),
    );
  }, [members, searchQuery]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadMembers} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('platformMembers.headings.list')}</h1>
        <button
          type="button"
          disabled
          title={t('platformMembers.tooltips.inviteDisabled')}
          className="px-4 py-2 bg-hairline-strong text-ink-muted rounded-sm text-sm font-medium cursor-not-allowed"
        >
          {t('platformMembers.actions.invite')}
        </button>
      </div>

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('platformMembers.filters.searchPlaceholder')}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredMembers}
        rowKey={(m) => m.id}
        emptyMessage={t('platformMembers.empty')}
        ariaLabel={t('platformMembers.ariaLabel')}
      />
    </div>
  );
}
